'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Card } from '@/components/common/Card';
import {
  Calendar,
  Users,
  Trophy,
  Wallet,
  MapPin,
  UserPlus,
  CheckCircle,
  Hourglass,
  Tag,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils/format';
import { TournamentStatusBadge } from './TournamentStatusBadge';
import { TournamentActions } from './TournamentActions';
import { AppImage } from '@/components/common/AppImage';
import { ShareButton } from '@/components/common/ShareButton';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { useAuth } from '@/hooks/useAuth';
import { tournamentService } from '@/services/api/tournaments';
import { handleError } from '@/lib/utils/error';
import type { Tournament } from '@/lib/types/auction';
import { playerService } from '@/services/api/players';
import type { SportSpecificFormData } from '@/components/enrollment/SportSpecificForms';
import { EnhancedEnrollmentModal } from '@/components/enrollment/EnhancedEnrollmentModal';
import type { ProfileDetailsData } from '@/components/enrollment/ProfileDetailsForm';
import { authService } from '@/services/api/auth';

interface TournamentDetailsProps {
  tournament: Tournament;
  onAction?: () => void;
}

export function TournamentDetails({
  tournament,
  onAction,
}: TournamentDetailsProps) {
  const { user, isAuthenticated, updateUser } = useAuth();
  const router = useRouter();
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentStatus, setEnrollmentStatus] = useState<
    null | 'PENDING' | 'APPROVED' | 'REJECTED' | 'NOT_ENROLLED'
  >(null);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [showSportModal, setShowSportModal] = useState(false);
  const [existingSportData, setExistingSportData] = useState<
    SportSpecificFormData | undefined
  >();

  // Cleanup modal state when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      // User logged out - close any open modals
      setShowSportModal(false);
      setIsEnrolling(false);
      setEnrollmentError(null);
    }
  }, [isAuthenticated]);

  const {
    name,
    type,
    sport,
    description,
    startDate,
    endDate,
    registrationDeadline,
    status,
    teams,
    baseBudget,
    totalTeams,
    maxPlayersPerTeam,
    location,
    owner,
    auctionDate,
  } = tournament;

  const playersBought = teams.reduce((acc, team) => acc + team.playerCount, 0);
  const totalPlayers = totalTeams * maxPlayersPerTeam;

  // Determine user roles regarding this specific tournament
  const isTournamentOwner =
    isAuthenticated && user ? user.id === owner?.id : false;
  const isTeamOwnerInThisTournament =
    isAuthenticated && user
      ? teams.some((team) => team.ownerId === user.id)
      : false;

  // Effect to check initial enrollment status for the current user
  useEffect(() => {
    const checkEnrollmentStatus = async () => {
      if (
        isAuthenticated &&
        user &&
        !isTournamentOwner &&
        !isTeamOwnerInThisTournament
      ) {
        try {
          const enrollment = await tournamentService.getMyEnrollment(
            tournament.id
          );
          setEnrollmentStatus(enrollment?.status || 'NOT_ENROLLED');
          // Store existing sport-specific data if available
          if (enrollment?.sportDetails) {
            setExistingSportData(enrollment.sportDetails);
          } else if (user.sportDetails && user.sportDetails?.[sport]) {
            // Fallback to user sport details if no enrollment exists
            setExistingSportData(user.sportDetails[sport]);
          }
        } catch (err) {
          // If no enrollment exists, set to NOT_ENROLLED
          setEnrollmentStatus('NOT_ENROLLED');
          if (user.sportDetails && user.sportDetails?.[sport]) {
            // Fallback to user sport details if no enrollment exists
            setExistingSportData(user.sportDetails[sport]);
          }
        }
      } else {
        setEnrollmentStatus(null); // Not authenticated or is owner/team owner
      }
    };

    checkEnrollmentStatus();
  }, [
    tournament.id,
    user,
    isAuthenticated,
    isTournamentOwner,
    isTeamOwnerInThisTournament,
  ]);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      router.push(
        `/auth/login?redirectUrl=${encodeURIComponent(window.location.pathname)}`
      );
      return;
    }

    // Show sport-specific modal for logged-in users
    setShowSportModal(true);
  };

  const handleSportSpecificSubmit = async (data: {
    profileDetails?: ProfileDetailsData;
    sportDetails: SportSpecificFormData;
  }) => {
    setIsEnrolling(true);
    setEnrollmentError(null);
    try {
      const sport = tournament.sport;

      // If profile details were provided, update the user profile first
      if (data.profileDetails && user) {
        try {
          const updatedUser = await authService.updateProfile(user.id, {
            firstName: data.profileDetails.name?.split(' ')[0],
            lastName: data.profileDetails.name?.split(' ').slice(1).join(' '),
            profilePicture: data.profileDetails.profilePicture,
            dateOfBirth: data.profileDetails.dateOfBirth?.toISOString(),
            gender: data.profileDetails.gender,
            // Parse phone number to extract country code and mobile number
            countryCode: data.profileDetails.countryCode
              ? data.profileDetails.countryCode
              : undefined,
            mobileNumber: data.profileDetails.mobileNumber,
          });

          // Update the auth context with the new user data
          updateUser?.(updatedUser);
        } catch (profileError) {
          console.warn(
            'Profile update failed, continuing with enrollment:',
            profileError
          );
          // Continue with enrollment even if profile update fails
        }
      }

      const enrollment = await playerService.requestEnrollment({
        tournamentId: tournament.id,
        sportDetails: {
          [sport]: data.sportDetails,
        },
      });
      setEnrollmentStatus(enrollment.status);
      setShowSportModal(false);
      onAction?.();
    } catch (err) {
      const errorMessage = handleError(err, 'Enrolling in tournament');
      setEnrollmentError(errorMessage);
      if (errorMessage.toLowerCase().includes('already pending')) {
        setEnrollmentStatus('PENDING');
        setShowSportModal(false);
      } else if (
        errorMessage.toLowerCase().includes('already approved') ||
        errorMessage.toLowerCase().includes('already enrolled')
      ) {
        setEnrollmentStatus('APPROVED');
        setShowSportModal(false);
      }
    } finally {
      setIsEnrolling(false);
    }
  };

  // Helper function to format tournament type
  const formatTournamentType = (type: string) => {
    return type === 'OPEN' ? 'Open Tournament' : 'Private Tournament';
  };

  // Helper function to format sport name
  const formatSport = (sport: string) => {
    return sport.charAt(0).toUpperCase() + sport.slice(1);
  };

  // Determine enrollment button state
  let enrollmentButton = null;
  if (tournament.status === 'REGISTRATION') {
    if (isAuthenticated) {
      if (isTournamentOwner || isTeamOwnerInThisTournament) {
        // Tournament owners and team owners don't see enrollment button
        enrollmentButton = null;
      } else {
        // Regular authenticated users
        if (
          enrollmentStatus === 'NOT_ENROLLED' ||
          enrollmentStatus === 'REJECTED'
        ) {
          enrollmentButton = (
            <Button
              onClick={handleEnroll}
              isLoading={isEnrolling}
              variant="primary"
              leftIcon={<UserPlus className="h-4 w-4" />}
              className="bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800"
            >
              Enroll Now
            </Button>
          );
        } else if (enrollmentStatus === 'PENDING') {
          enrollmentButton = (
            <Button
              variant="light"
              disabled
              leftIcon={<Hourglass className="h-4 w-4" />}
              className="text-primary-700"
            >
              Enrollment Pending
            </Button>
          );
        } else if (enrollmentStatus === 'APPROVED') {
          enrollmentButton = (
            <Button
              variant="primary"
              disabled
              leftIcon={<CheckCircle className="h-4 w-4" />}
              className="bg-green-600 text-white hover:bg-green-600"
            >
              Enrolled
            </Button>
          );
        }
      }
    } else {
      // Not authenticated - show CTA button
      enrollmentButton = (
        <Button
          onClick={handleEnroll}
          variant="primary"
          leftIcon={<UserPlus className="h-4 w-4" />}
          className="bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800"
        >
          Sign in to Enroll
        </Button>
      );
    }
  }

  return (
    <div className="relative">
      <Card className="rounded-none border-none hover:translate-y-0">
        <div>
          <div className="flex flex-col items-start justify-between space-y-4 p-2 sm:flex-row sm:items-center sm:space-y-0">
            <div className="max-w-2xl">
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                {name}
              </h1>
              {description && (
                <p className="mt-1 line-clamp-2 text-sm text-gray-500 sm:text-base">
                  {description}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <TournamentStatusBadge status={status} />
              <ShareButton
                title={`${tournament.name} - Tournament`}
                description={`Join ${tournament.name} on Auction Chase - ${tournament.description || `A tournament with ${teams.length} teams and a base budget of ${formatCurrency(baseBudget)}. Registration deadline: ${new Date(registrationDeadline).toLocaleDateString()}`}`}
                url={`${window.location.origin}/tournament/${tournament.id}`}
                hashtags={['AuctionChase', 'Tournament', 'SportsAuction']}
                variant="icon"
                image={`${window.location.origin}/images/og.jpg`}
              />
              {enrollmentButton}
              <TournamentActions tournament={tournament} onAction={onAction} />
            </div>
          </div>
          {enrollmentError && (
            <div className="p-2">
              <Alert type="error" message={enrollmentError} />
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="relative h-10 w-10 overflow-hidden rounded-full">
                <AppImage
                  src={owner?.profilePicture || ''}
                  alt={owner?.name || 'Tournament Admin'}
                  gradient
                  size={'sm'}
                  fallbackText={owner?.name}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Tournament Organizer
                </p>
                <p className="font-medium text-gray-900">
                  {owner?.name || 'Tournament Admin'}
                </p>
                {owner?.emailVerified && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-primary-600">✓ Verified</span>
                  </div>
                )}
              </div>
            </div>

            {type && (
              <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                  <Tag className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Type</p>
                  <p className="font-medium text-gray-900">
                    {formatTournamentType(type)}
                  </p>
                </div>
              </div>
            )}

            {sport && (
              <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-blue/10">
                  <Zap className="h-5 w-5 text-accent-blue" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Sport</p>
                  <p className="font-medium text-gray-900">
                    {formatSport(sport)}
                  </p>
                </div>
              </div>
            )}

            {location && (
              <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                  <MapPin className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Location</p>
                  <p className="font-medium capitalize text-gray-900">
                    {location}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <Calendar className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Registration Deadline
                </p>
                <p className="font-medium text-gray-900">
                  {format(new Date(registrationDeadline), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <Calendar className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Auction Date
                </p>
                <p className="font-medium text-gray-900">
                  {format(new Date(auctionDate), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <Calendar className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Start Date</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(startDate), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <Calendar className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">End Date</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(endDate), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-blue/10">
                <Users className="h-5 w-5 text-accent-blue" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Teams</p>
                <p className="font-medium text-gray-900">
                  {teams.length} / {totalTeams}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-4 transition-all duration-300 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-purple/10">
                <Trophy className="h-5 w-5 text-accent-purple" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Players Bought
                </p>
                <p className="font-medium text-gray-900">
                  {playersBought} / {totalPlayers}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg bg-gradient-to-r from-accent-blue/5 via-accent-purple/5 to-accent-green/5 p-5">
            <div className="flex items-center space-x-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-green/10">
                <Wallet className="h-6 w-6 text-accent-green" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Base Budget</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(baseBudget)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Enhanced enrollment modal with profile details */}
      <EnhancedEnrollmentModal
        isOpen={showSportModal}
        onClose={() => setShowSportModal(false)}
        sport={sport}
        tournamentName={name}
        onSubmit={handleSportSpecificSubmit}
        isLoading={isEnrolling}
      />
    </div>
  );
}
